import { UserManager, WebStorageStateStore } from 'oidc-client-ts';

/**
 * Public-client OIDC Authorization Code flow with PKCE, run entirely in the
 * browser via oidc-client-ts. The SPA talks to Keycloak directly. The
 * UserManager is the single source of truth for tokens (persisted in its
 * sessionStorage userStore); consumers read the access token via the async
 * accessors below. No backend involvement in the auth handshake.
 *
 * Keycloak requirements:
 *  - 'dto' client must be a PUBLIC client with PKCE (S256) enabled.
 *  - The SPA origin (window.location.origin + '/') must be a registered valid
 *    redirect URI and a Web Origin (for CORS on the token/metadata endpoints).
 */

const AUTHORITY = (import.meta.env.VITE_KEYCLOAK_AUTHORITY as string | undefined)?.replace(/\/$/, '') ?? '';
const CLIENT_ID = (import.meta.env.VITE_KEYCLOAK_CLIENT_ID as string | undefined) ?? '';
const SCOPE = (import.meta.env.VITE_KEYCLOAK_SCOPE as string | undefined) ?? 'openid profile email';

/** Redirect URI is the SPA root on the current origin; must be registered in Keycloak. */
const REDIRECT_URI = `${window.location.origin}/`;

const userManager = new UserManager({
  authority: AUTHORITY,
  client_id: CLIENT_ID,
  redirect_uri: REDIRECT_URI,
  post_logout_redirect_uri: REDIRECT_URI,
  response_type: 'code',
  scope: SCOPE,
  userStore: new WebStorageStateStore({ store: window.sessionStorage }),
  automaticSilentRenew: true,
});

userManager.events.addUserLoaded((user) => {
  console.debug('[oidc] user loaded', { sub: user.profile.sub, expires_at: user.expires_at });
});
userManager.events.addUserUnloaded(() => {
  console.debug('[oidc] user unloaded');
});
userManager.events.addAccessTokenExpired(() => {
  console.debug('[oidc] access token expired — attempting silent renew');
  void userManager.signinSilent().catch(() => {
    console.debug('[oidc] silent renew failed — clearing session');
    void userManager.removeUser();
  });
});

/** Returns true when both Keycloak authority and client ID are configured. */
export function isAuthConfigured(): boolean {
  return Boolean(AUTHORITY && CLIENT_ID);
}

/** Current access token from the UserManager, or null if signed out/expired. */
export async function getAccessToken(): Promise<string | null> {
  const user = await userManager.getUser();
  return user && !user.expired ? user.access_token : null;
}

/** True when a valid token exists and expires within the next 60 seconds. */
export async function isTokenExpiringSoon(): Promise<boolean> {
  const user = await userManager.getUser();
  return Boolean(user && user.expires_in != null && user.expires_in <= 60);
}

/** True when a non-expired user session is present. */
export async function isAuthenticated(): Promise<boolean> {
  const user = await userManager.getUser();
  return Boolean(user && !user.expired);
}

/** Removes the stored user session (local sign-out). */
export async function clearSession(): Promise<void> {
  await userManager.removeUser();
}

/** Redirects the browser to Keycloak's authorization endpoint (PKCE). */
export async function beginLogin(): Promise<void> {
  console.debug('[oidc] beginLogin → redirecting to IDP', { authority: AUTHORITY, client_id: CLIENT_ID });
  await userManager.signinRedirect();
}

/**
 * If the current URL is a Keycloak redirect (?code & ?state), completes the code
 * exchange, mirrors tokens, and strips the auth params from the address bar.
 * Otherwise loads any existing stored user. Returns true when authenticated.
 */
export async function completeLoginIfCallback(): Promise<boolean> {
  const query = new URLSearchParams(window.location.search);
  const isCallback = query.has('code') && query.has('state');

  if (!isCallback) {
    console.debug('[oidc] completeLoginIfCallback — not a callback, loading stored user');
    const existing = await userManager.getUser();
    const authenticated = Boolean(existing && !existing.expired);
    console.debug('[oidc] stored user authenticated:', authenticated);
    return authenticated;
  }

  console.debug('[oidc] completeLoginIfCallback — handling IDP callback (code exchange)');
  try {
    const user = await userManager.signinRedirectCallback();
    console.debug('[oidc] code exchange succeeded', { sub: user.profile.sub });
    return Boolean(user && !user.expired);
  } catch (err) {
    console.debug('[oidc] code exchange failed', err);
    return false;
  } finally {
    window.history.replaceState(null, '', window.location.pathname);
  }
}

/** Refreshes tokens directly against Keycloak (refresh-token grant). */
export async function refreshTokens(): Promise<boolean> {
  console.debug('[oidc] refreshTokens — requesting silent renew');
  try {
    const user = await userManager.signinSilent();
    console.debug('[oidc] refreshTokens — succeeded');
    return Boolean(user && !user.expired);
  } catch (err) {
    console.debug('[oidc] refreshTokens — failed', err);
    return false;
  }
}

/** Clears local tokens and redirects to Keycloak's end-session endpoint. */
export async function beginLogout(): Promise<void> {
  console.debug('[oidc] beginLogout → redirecting to IDP end-session');
  const user = await userManager.getUser();

  try {
    await userManager.signoutRedirect({ id_token_hint: user?.id_token });
  } catch (err) {
    console.debug('[oidc] beginLogout — signoutRedirect failed, falling back to root redirect', err);
    await userManager.removeUser();
    window.location.href = REDIRECT_URI;
  }
}
